import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, loginSchema } from "@utils/index";
import { LoginFormInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { useUserLogin } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormHeaderNotAuth,
  FormRow,
  FormInput,
  Button,
  AuthLinks,
  RegistrationButton,
} from "@components/index";

export const LoginForm = () => {
  const isAuth = useCommonStore(({ isAuth }) => isAuth);
  const setCommonState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();

  const { mutate: mutateUserLogin, isPending: isPendingUserLogin } =
    useUserLogin(
      () => {
        reset();
        setCommonState({ isAuth: true });
        notify("success", "Welcome here!");
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "email" | "password", { message: fields![key] }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setCommonState({ isLoading: isPendingUserLogin });
  }, [isPendingUserLogin, setCommonState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
  } = useForm<LoginFormInterface>({
    mode: "onBlur",
    disabled: isPendingUserLogin,
    resolver: yupResolver(loginSchema),
  });

  const onSubmit: SubmitHandler<LoginFormInterface> = (fields) =>
    mutateUserLogin({ ...fields });

  if (isAuth) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="md:mx-auto md:max-w-[440px] md:pt-[218px]"
      >
        <FormHeaderNotAuth
          title="Sign In"
          subTitle="Enter your details below"
          linkTitle="Forgot password?"
          linkHref="/password-forgot"
        />
        <FormRow top="medium">
          <FormInput
            {...register<"email">("email")}
            name="email"
            label="Email"
            error={formState.errors.email?.message}
            onChange={({ currentTarget }) => {
              setValue("email", currentTarget.value.trim());
              clearErrors("email");
            }}
          />
          <FormInput
            {...register<"password">("password")}
            type="password"
            name="password"
            label="Password"
            error={formState.errors.password?.message}
            onChange={({ currentTarget }) => {
              setValue("password", currentTarget.value.trim());
              clearErrors("password");
            }}
          />
        </FormRow>
        <FormRow top="medium">
          <Button type="submit" title="Sign in" />
        </FormRow>
        {isMobile && (
          <FormRow top="large">
            <AuthLinks />
          </FormRow>
        )}
      </Form>
      {isMobile && <RegistrationButton />}
      {!isMobile && <AuthLinks />}
    </>
  );
};
